import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, IconButton, CircularProgress } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
// import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { catalogueService } from '../../services/catalogue.service';
import { toast } from 'react-toastify';
import { useMessageHandler } from '../../hooks/useMessageHandler';
import {
  formatOptionOrItemLabel,
  formatPackageLocaleFields,
  formatProductLocaleField,
} from '../../utils/bilingualDisplay';

const ServiceDetail: React.FC = () => {
  const handleMessage = useMessageHandler();
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [service, setService] = useState<any>(null);
  const [versionId, setVersionId] = useState<number>(1);
  const [approving, setApproving] = useState(false);
  const [suspending, setSuspending] = useState(false);

  useEffect(() => {
    const fetchService = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const response = await catalogueService.getServiceById(id);
        // Backend returns { data: { versionId, details: {...} } }
        // We need the details object which contains the product info
        const productData = response.data?.details || response.data?.product || null;
        setService(productData);
        setVersionId(response.data?.versionId || 1);
      } catch (error) {
        console.error('Error fetching service:', error);
        toast.error('Failed to load service details');
      } finally {
        setLoading(false);
      }
    };

    fetchService();
  }, [id]);

    const navigateToServices = useCallback(() => {
      navigate('/catalogue?tab=services');
    }, [navigate])
  
  const handleApprove = async () => {
    if (!service || !id) return;
    
    setApproving(true);

    const response = await catalogueService.approveService(id, versionId);
    
    if (response.ok) {
      if (response.messageInfo) {
        handleMessage(response.messageInfo)
      }
    } else {
      handleMessage({
        error: response.error
      })
    }

    setApproving(false);

    navigateToServices();
  };

  const handleSuspend = async () => {
    if (!service || !id) return;
        
    setSuspending(true);

    const response = await catalogueService.suspendService(id, versionId);
    
    if (response.ok) {
      if (response.messageInfo) {
        handleMessage(response.messageInfo)
      }
    } else {
      handleMessage({
        error: response.error
      })
    }

    setSuspending(false);

    navigateToServices();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!service) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Typography>Service not found</Typography>
      </Box>
    );
  }

  const displayTitle = formatProductLocaleField(service.title, service.locale, 'title');
  const displayShortDescription = formatProductLocaleField(
    service.shortDescription,
    service.locale,
    'shortDescription',
  );
  const displayFullDescription = formatProductLocaleField(
    service.fullDescription,
    service.locale,
    'fullDescription',
  );

  const PropertyRow = ({ label, value }: { label: string; value: string | number }) => (
    <Box sx={{ display: 'flex', gap: 'var(--palette-light-1-value)', padding: 'var(--palette-light-2-value) 0', borderBottom: '1px solid #F1F4F4' }}>
      <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-secondary-value-hex)', flex: 1 }}>{label}</Typography>
      <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>{value}</Typography>
    </Box>
  );

  const disableActions = approving || suspending;

  return (
    <Box
      sx={{
        padding: {
          xs: '24px 16px',
          sm: '40px 40px',
          md: '40px 80px',
        },
        gap: 'var(--palette-light-6-value)',
        width: '100%',
        minHeight: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto'
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--palette-light-4-value)' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-2-value)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 'var(--palette-light-3-value)' }}>
            <IconButton
              onClick={navigateToServices}
              sx={{
                width: 'var(--palette-light-8-value)',
                height: 'var(--palette-light-8-value)',
                border: '1px solid var(--palette-light-ios-separator-value-hex)',
                borderRadius: 'var(--palette-light-4-value)',
                color: 'var(--palette-light-brand-primary-value-hex)'
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
            <Box>
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-h1-value)', fontWeight: 700, lineHeight: 1.15, color: 'var(--palette-light-text-primary-value-hex)' }}>
                {displayTitle || 'Service'}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 'var(--palette-light-2-value)' }}>
          <Button
            variant="contained"
            loading={approving}
            disabled={disableActions}
            onClick={handleApprove}
            sx={{
              backgroundColor: 'var(--palette-light-brand-primary-value-hex)',
              color: 'var(--palette-light-text-foreground-value-hex)',
              textTransform: 'none',
              fontSize: 'var(--palette-light-font-size-default-value)',
              fontWeight: 700,
              borderRadius: 'var(--palette-light-4-value)',
              padding: '2px 32px 0',
              height: 'var(--palette-light-14-value)',
              '&:hover': {
                backgroundColor: 'var(--palette-light-brand-primary-value-hex)',
                opacity: 0.9
              }
            }}
          >
            Approve
          </Button>
          <Button
            variant="outlined"
            loading={suspending}
            disabled={disableActions}
            onClick={handleSuspend}
            sx={{
              borderColor: 'var(--palette-light-brand-primary-value-hex)',
              color: 'var(--palette-light-brand-primary-value-hex)',
              textTransform: 'none',
              fontSize: 'var(--palette-light-font-size-default-value)',
              fontWeight: 700,
              borderRadius: 'var(--palette-light-4-value)',
              padding: '2px 32px 0',
              height: 'var(--palette-light-14-value)',
              '&:hover': {
                borderColor: 'var(--palette-light-brand-primary-value-hex)',
                backgroundColor: 'rgba(103, 49, 79, 0.04)'
              }
            }}
          >
            Suspend
          </Button>
          {/* <IconButton
            sx={{
              width: 'var(--palette-light-14-value)',
              height: 'var(--palette-light-14-value)',
              border: '1px solid var(--palette-light-brand-primary-value-hex)',
              borderRadius: 'var(--palette-light-4-value)',
              color: 'var(--palette-light-brand-primary-value-hex)'
            }}
          >
            <MoreHorizIcon />
          </IconButton> */}
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ display: 'flex', gap: 'var(--palette-light-10-value)', flexWrap: 'wrap' }}>
        {/* Left column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-8-value)', minWidth: '332px', maxWidth: '500px', flex: 1 }}>
          {/* Vendor Info */}
          {service.vendorName && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-2-value)' }}>
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
                Vendor
              </Typography>
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-brand-primary-value-hex)', fontWeight: 600 }}>
                {service.vendorName}
              </Typography>
            </Box>
          )}

          {/* Image Section */}
          {service.imageURLs && service.imageURLs.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-4-value)' }}>
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-h4-value)', fontWeight: 700, color: 'var(--palette-light-text-primary-value-hex)' }}>
                Image
              </Typography>
              <Box
                sx={{
                  width: '100%',
                  height: '333px',
                  borderRadius: 'var(--palette-light-2-value)',
                  backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
                  backgroundImage: `url(${service.imageURLs[0]})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  border: '1px solid #C5CBD3'
                }}
              />
            </Box>
          )}

          {/* Description Section */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-4-value)' }}>
            <Typography sx={{ fontSize: 'var(--palette-light-font-size-h4-value)', fontWeight: 700, color: 'var(--palette-light-text-primary-value-hex)' }}>
              Description
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-1-value)', padding: 'var(--palette-light-3-value)', backgroundColor: 'var(--palette-light-background-secondary-value-hex)', borderRadius: 'var(--palette-light-3-value)' }}>
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
                Service title
              </Typography>
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                {displayTitle || 'N/A'}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-1-value)', padding: 'var(--palette-light-3-value)', backgroundColor: 'var(--palette-light-background-secondary-value-hex)', borderRadius: 'var(--palette-light-3-value)' }}>
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
                Short description
              </Typography>
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                {displayShortDescription || 'N/A'}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-1-value)', padding: 'var(--palette-light-3-value)', backgroundColor: 'var(--palette-light-background-secondary-value-hex)', borderRadius: 'var(--palette-light-3-value)' }}>
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
                Full description
              </Typography>
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                {displayFullDescription || 'N/A'}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-1-value)', padding: 'var(--palette-light-3-value)', backgroundColor: 'var(--palette-light-background-secondary-value-hex)', borderRadius: 'var(--palette-light-3-value)' }}>
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
                Base price
              </Typography>
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                QAR {service.minPrice || 0}
              </Typography>
            </Box>
          </Box>

          {/* Requirements Section */}
          {service.requirements && service.requirements.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-4-value)' }}>
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-h4-value)', fontWeight: 700, color: 'var(--palette-light-text-primary-value-hex)' }}>
                Requirements
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-2-value)' }}>
                {service.requirements.map((req: any, index: number) => (
                  <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 'var(--palette-light-2-value) 0', borderBottom: '1px solid var(--palette-light-border-light-value-hex)' }}>
                    <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                      {req.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 'var(--palette-light-2-value)' }}>
                      <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                        {req.amount} {req.unit}
                      </Typography>
                      {req.required && (
                        <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-brand-primary-value-hex)' }}>
                          Required
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>

        {/* Right column - Packages */}
        {service.packages && service.packages.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-4-value)', flex: 1, minWidth: '300px' }}>
            <Typography sx={{ fontSize: 'var(--palette-light-font-size-h4-value)', fontWeight: 700, color: 'var(--palette-light-text-primary-value-hex)' }}>
              Packages and prices
            </Typography>

            {service.packages.map((pkg: any, index: number) => {
              const { title: pkgTitle, subTitle: pkgSub } = formatPackageLocaleFields({
                ...pkg,
                title: pkg.title || pkg.name || '',
              });
              return (
              <Box key={index} sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-2-value)', padding: 'var(--palette-light-4-value)', backgroundColor: 'var(--palette-light-background-secondary-value-hex)', borderRadius: 'var(--palette-light-3-value)' }}>
                <Typography sx={{ fontSize: 'var(--palette-light-font-size-h5-value)', fontWeight: 600, color: 'var(--palette-light-text-primary-value-hex)' }}>
                  {pkgTitle || `Package ${index + 1}`}
                </Typography>
                
                {pkgSub ? (
                  <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
                    {pkgSub}
                  </Typography>
                ) : null}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-1-value)' }}>
                  <PropertyRow label="Type" value={pkg.type || 'N/A'} />
                  {pkg.required !== undefined && <PropertyRow label="Required" value={pkg.required ? 'Yes' : 'No'} />}
                  {pkg.maxSelections && <PropertyRow label="Max selections" value={pkg.maxSelections} />}
                  
                  {/* Display options for one-select/multi-select */}
                  {pkg.options && pkg.options.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)', mb: 1 }}>
                        Options:
                      </Typography>
                      {pkg.options.map((opt: any, optIndex: number) => (
                        <Box key={optIndex} sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                          <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)' }}>
                            {formatOptionOrItemLabel(opt.label, opt.locale)}
                          </Typography>
                          {opt.price !== undefined && (
                            <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 600 }}>
                              QAR {opt.price}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* Display items for addons */}
                  {pkg.items && pkg.items.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)', mb: 1 }}>
                        Items:
                      </Typography>
                      {pkg.items.map((item: any, itemIndex: number) => (
                        <Box key={itemIndex} sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                          <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)' }}>
                            {formatOptionOrItemLabel(item.label, item.locale)}
                          </Typography>
                          <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 600 }}>
                            QAR {item.pricePerUnit}/unit ({item.minUnits}-{item.maxUnits})
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ServiceDetail;
